import { useI18n } from "@/i18n/I18nProvider";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Facebook, Instagram, Linkedin, ArrowRight, ArrowLeft, Youtube, Mail, MapPin, Link as LinkIcon } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { addNewsletterSubscriber } from "@/data/newsletterStore";
import { getCmsLayout } from "@/data/cmsStore";
import { useSettingsStore } from "@/data/settingsStore";
import ContactFormDialog from "./ContactFormDialog";
import { XIcon } from "./icons/XIcon";

// TikTok icon SVG as Lucide doesn't have it natively
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} stroke="none">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.01.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.12-3.44-3.1-3.66-5.38-.2-2.14.73-4.24 2.45-5.55 1.54-1.18 3.59-1.49 5.48-1.1v4.21c-1.07-.36-2.31-.19-3.13.56-.91.82-1.13 2.21-.6 3.26.54 1.1 1.99 1.62 3.16 1.18.91-.34 1.52-1.22 1.64-2.18.04-.54.02-1.09.02-1.63V.02z"/>
  </svg>
);

const getIcon = (name: string, props: any) => {
  switch (name.toLowerCase()) {
    case 'facebook': return <Facebook {...props} />;
    case 'instagram': return <Instagram {...props} />;
    case 'linkedin': return <Linkedin {...props} />;
    case 'tiktok': return <TikTokIcon {...props} />;
    case 'youtube': return <Youtube {...props} />;
    case 'mail': return <Mail {...props} />;
    case 'map-pin': return <MapPin {...props} />;
    case 'x':
    case 'twitter': return <XIcon {...props} />;
    default: return <LinkIcon {...props} />;
  }
};

const Footer = () => {
  const { t, lang, isRtl } = useI18n();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const settings = useSettingsStore(state => state.settings);
  const getSettingReactive = (key: string, fallback: string) => settings[key] !== undefined ? settings[key] : fallback;

  const bannerVisible = getSettingReactive("kaynin_footer_banner_visible", "false") === "true";
  
  const bannerContentFr = getSettingReactive("kaynin_footer_banner_content", "");
  const bannerContentAr = getSettingReactive("kaynin_footer_banner_content_ar", "");
  const bannerContentZgh = getSettingReactive("kaynin_footer_banner_content_zgh", "");
  
  let bannerContent = bannerContentFr;
  if (lang === 'ar' && bannerContentAr) bannerContent = bannerContentAr;
  if (lang === 'zgh' && bannerContentZgh) bannerContent = bannerContentZgh;
  
  const bannerBg = getSettingReactive("kaynin_footer_banner_bg", "bg-[#0a1f18]");

  const handleSubscribe = async () => {
    if (!email || !email.includes("@")) {
      toast({
        title: lang === 'ar' ? "خطأ" : "Erreur",
        description: lang === 'ar' ? "يرجى إدخال بريد إلكتروني صحيح." : "Veuillez entrer une adresse e-mail valide.",
        variant: "destructive"
      });
      return;
    }
    
    const added = await addNewsletterSubscriber(email);
    if (added) {
      toast({
        title: lang === 'ar' ? "شكراً لك!" : "Merci !",
        description: lang === 'ar' ? "تم تسجيلك بنجاح في نشرتنا الإخبارية." : "Vous êtes bien inscrit à notre newsletter.",
      });
      setEmail("");
    } else {
      toast({
        title: lang === 'ar' ? "عذراً" : "Désolé",
        description: lang === 'ar' ? "أنت مسجل بالفعل بهذه العناوين." : "Vous êtes déjà inscrit avec cette adresse e-mail.",
        variant: "destructive"
      });
    }
  };

  const layout = getCmsLayout("global");
  const footerSection = layout.find(s => s.id === "footer");
  const cmsContent = footerSection?.content || {};

  const cta = {
    title: lang === 'ar' ? (cmsContent.cta_title_ar || "مستعد للمشاركة في بناء مغرب الغد؟") 
         : lang === 'zgh' ? (cmsContent.cta_title_zgh || "ⵉⵔⵎⴰⵙⵏ ⴰⴷ ⵜⵜⴽⴽⵉⵜ ⴳ ⵓⴱⵏⵉ ⵏ ⵍⵎⵖⵔⵉⴱ ⵏ ⵓⵣⴽⴽⴰ?")
         : (cmsContent.cta_title_fr || "Prêt à participer à la construction du Maroc de demain ?"),
    btn1: lang === 'ar' ? (cmsContent.cta_btn1_ar || "أريد المشاركة") 
         : lang === 'zgh' ? (cmsContent.cta_btn1_zgh || "ⵔⵉⵖ ⴰⴷ ⴰⵡⵙⵖ")
         : (cmsContent.cta_btn1_fr || "Je veux m'engager"),
    btn2: lang === 'ar' ? (cmsContent.cta_btn2_ar || "أريد أن ألاحظ") 
         : lang === 'zgh' ? (cmsContent.cta_btn2_zgh || "ⵔⵉⵖ ⴰⴷ ⵜⵜⴽⴽⵉⵖ")
         : (cmsContent.cta_btn2_fr || "Je veux observer")
  };

  const desc = lang === 'ar' ? (cmsContent.desc_ar || "الفضاء المواطن من الجيل الجديد.")
             : lang === 'zgh' ? (cmsContent.desc_zgh || "ⴰⵙⴰⵢⵔⴰⵔ ⴰⵏⴰⵎⵓⵔ ⵏ ⵜⴰⵙⵓⵜ ⵜⴰⵎⴰⵢⵏⵓⵜ.")
             : (cmsContent.desc_fr || "Kayn'in, L'espace citoyen nouvelle génération.");

  let customLinks: Array<{ labelFr: string, labelAr: string, labelZgh?: string, url: string }> = [];
  let socials: any[] = [];
  let contacts: any[] = [];
  try {
    if (cmsContent.links) {
      customLinks = JSON.parse(cmsContent.links);
    }
    if (cmsContent.socials) {
      socials = JSON.parse(cmsContent.socials).filter((s:any) => s.visible !== false);
    }
    if (cmsContent.contacts) {
      contacts = JSON.parse(cmsContent.contacts).filter((c:any) => c.visible !== false);
    }
  } catch (e) {
    console.error("Error parsing footer data");
  }

  // Fallbacks
  if (!customLinks || customLinks.length === 0) {
    customLinks = [
      { labelFr: "Inscription électorale", labelAr: "التسجيل الانتخابي", labelZgh: "ⴰⵙⵖⵍ ⴰⵏⵙⵜⴰⵢ", url: "/ressources/inscription" },
      { labelFr: "Guide du candidat", labelAr: "دليل المرشح", labelZgh: "ⴰⵎⵔⵙⵉ ⵏ ⵓⵎⵙⵓⵜⵔ", url: "/ressources/candidature" },
      { labelFr: "Partis politiques", labelAr: "الأحزاب السياسية", labelZgh: "ⵉⵎⵓⵔⴰⵏ ⵉⵙⵔⵜⴰⵏⵏ", url: "/partis" },
      { labelFr: "Actions citoyennes", labelAr: "الأعمال المواطنة", labelZgh: "ⵜⵉⴳⴰⵡⵉⵏ ⵏ ⵜⵎⵓⵔⵜ", url: "/actions" },
      { labelFr: "Observateur Électoral", labelAr: "مراقب انتخابي", labelZgh: "ⴰⵎⴰⵏⴰⵢ ⴰⵏⵙⵜⴰⵢ", url: "/observateur" }
    ];
  }

  if (socials.length === 0) {
    socials = [
      { platform: "Facebook", icon: "facebook", url: "#" },
      { platform: "Instagram", icon: "instagram", url: "#" },
      { platform: "TikTok", icon: "tiktok", url: "#" },
      { platform: "LinkedIn", icon: "linkedin", url: "#" }
    ];
  }

  if (contacts.length === 0) {
    contacts = [
      { type: "Email", icon: "mail", value: "mycmchub@gmail.com", url: "mailto:mycmchub@gmail.com" },
      { type: "Location", icon: "map-pin", value: "Rabat, Maroc", url: "" }
    ];
  }

  const legalPagesSection = layout.find(s => s.id === "legal_pages");
  const legalContent = legalPagesSection?.content || {};
  const displayMode = legalContent.display_mode || "unified";
  
  const legalLinkTextUnified = lang === 'ar' ? 'الشروط والمعلومات القانونية' : lang === 'zgh' ? 'ⵉⵙⵓⵔⴰⴼ ⴷ ⵉⵏⵖⵎⵉⵙⵏ ⵉⵣⵔⴼⴰⵏⵏ' : 'Conditions et mentions légales';
  
  const mentionsTitle = legalContent[`mentions_title_${lang}`] || legalContent["mentions_title_fr"] || "Mentions légales";
  const conditionsTitle = legalContent[`conditions_title_${lang}`] || legalContent["conditions_title_fr"] || "Conditions d'utilisation";
  const politiqueTitle = legalContent[`politique_title_${lang}`] || legalContent["politique_title_fr"] || "Politique de confidentialité";

  return (
    <>
      {/* Pre-Footer Call to Action */}
      <section className="bg-primary pt-16 pb-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-accent/20 rounded-full blur-3xl -z-10" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-secondary/20 rounded-full blur-3xl -z-10" />
        
        <div className="container relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-extrabold text-primary-foreground tracking-tight mb-8 max-w-3xl mx-auto leading-tight">
            {cta.title}
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" variant="secondary" className="rounded-full font-bold px-8 h-12 text-sm" asChild>
              <Link to="/engagement">{cta.btn1}</Link>
            </Button>
            <Button size="lg" variant="outline" className="rounded-full font-bold px-8 h-12 text-sm bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary transition-colors" asChild>
              <Link to="/observateur">{cta.btn2}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Admin Announcement Banner */}
      {bannerVisible && bannerContent && (
        <section className={`${bannerBg} py-8 relative overflow-hidden flex items-center justify-center text-center px-4 shadow-inner border-y border-white/5`}>
          <div className="absolute inset-0 opacity-20 bg-[url('/noise.png')] mix-blend-overlay pointer-events-none"></div>
          <div 
            className="container relative z-10 font-body text-white/90 text-sm md:text-base leading-relaxed max-w-5xl mx-auto prose-invert prose-p:my-2 prose-a:text-primary prose-a:underline hover:prose-a:text-white transition-colors [&>h1]:text-2xl [&>h2]:text-xl [&>h1]:font-bold [&>h2]:font-bold"
            dangerouslySetInnerHTML={{ __html: bannerContent }} 
          />
        </section>
      )}

      {/* Main Footer */}
      <footer className="bg-foreground py-12 md:py-16 text-background relative">
        <div className="container px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12 text-center md:text-start transition-all">
            
            {/* Col 1: Brand & Desc */}
            <div className="space-y-6">
              <div>
                <Link to="/" dir="ltr" className="flex items-end gap-0 mb-4 hover:scale-105 transition-transform w-fit select-none pb-1 mx-auto md:mx-0">
                  <div className="flex flex-col justify-end">
                    <div dir="ltr" className="flex items-center justify-between w-full text-[8.5px] font-bold text-white/50 uppercase tracking-widest mb-[-2px]">
                      <span>ⴽⴰⵢⵏⵉⵏ</span>
                      <span className="w-[3px] h-[3px] rounded-full bg-[#ff6b00]"></span>
                      <span dir="rtl">كاينين</span>
                    </div>
                    <span dir="ltr" className="font-display font-black text-3xl leading-[0.9] text-white tracking-tight">Kayn</span>
                  </div>
                  <span className="w-1.5 h-3 bg-[#ff6b00] inline-block mb-[18px] mx-[3px] rounded-[1px] transform -skew-x-6"></span>
                  <span dir="ltr" className="font-display font-black text-3xl leading-[0.9] text-white tracking-tight">in</span>
                </Link>
                <p className="text-sm text-background/80 font-body leading-relaxed max-w-xs mx-auto md:mx-0">
                  {desc}
                </p>
              </div>
              
              <div className="flex justify-center md:justify-start gap-4">
                {socials.map((soc, i) => (
                  <a key={i} href={soc.url} aria-label={soc.platform} className="w-10 h-10 rounded-full bg-background/5 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors group">
                    {getIcon(soc.icon, { className: "w-5 h-5 opacity-70 group-hover:opacity-100 transition-opacity" })}
                  </a>
                ))}
              </div>
            </div>

            {/* Col 2: Navigation */}
            <div>
              <h4 className="font-body font-bold text-sm text-primary mb-6 uppercase tracking-wider">{t.footer.usefulLinks}</h4>
              <ul className="space-y-3 text-sm text-background/70 font-body">
                {customLinks.map((link, idx) => {
                  const label = lang === 'ar' ? link.labelAr : lang === 'zgh' ? link.labelZgh : link.labelFr;
                  return (
                    <li key={idx}>
                      <Link to={link.url} className="hover:text-primary transition-colors flex items-center justify-center md:justify-start group relative">
                        <div className="absolute top-1/2 -translate-y-1/2 -start-3 opacity-0 group-hover:opacity-100 transition-opacity hidden md:block">
                           <span className="w-1 h-1 rounded-full bg-primary block" />
                        </div>
                        <span>{label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Col 3: Contact */}
            <div>
              <h4 className="font-body font-bold text-sm text-primary mb-6 uppercase tracking-wider">{t.footer.contact}</h4>
              <ul className="space-y-3 text-sm text-background/70 font-body mb-4">
                <li className="flex items-center justify-center md:justify-start gap-2">
                  <Mail className="w-4 h-4 opacity-70" />
                  <ContactFormDialog>
                    <button className="hover:text-primary transition-colors text-left bg-transparent border-none p-0 cursor-pointer">
                      {lang === 'ar' ? 'اتصل بنا' : lang === 'zgh' ? 'ⴰⵡⴰⴹ ⴰⴽⴷ ⵏⵖ' : 'Nous contacter'}
                    </button>
                  </ContactFormDialog>
                </li>
              </ul>
              <p className="text-xs italic text-background/60 leading-relaxed">
                {lang === 'ar' ? "نسيت شيئاً؟ أشر إلى فكرتك. نحن نبني معاً." : 
                 lang === 'zgh' ? "ⵜⵜⵓⴷ ⴽⵔⴰ? ⴳⴰⵍⵍ ⵜⴰⵡⵏⴳⵉⵎⵜ ⵏⵏⴽ. ⴰⴷ ⵏⴱⵏⵓ ⴰⴽⴷ." :
                 "Un oubli ? Tag ton idée. On co‑construit."}
              </p>
            </div>

            {/* Col 4: Newsletter / Hook */}
            {cmsContent.newsletter_visible !== "false" && (
              <div className="space-y-6 bg-background/5 p-6 rounded-2xl border border-background/10 h-fit">
                <p className="text-[13px] italic font-body text-background/90 opacity-90 leading-relaxed font-light">
                  {lang === 'ar' ? (cmsContent.newsletter_text_ar || "*هنا، لا نتصفح فقط. نحن نناقش. ننشر. ونمارس السياسة ونبقى على تواصل.*") : 
                   lang === 'zgh' ? (cmsContent.newsletter_text_zgh || "*ⴷⴰ, ⵓⵔ ⵏⵣⵣⵔⴰⵢ ⴽⴰ. ⵏⵙⵎⵏⵉⴷ. ⵏⴼⵙⵙⵔ. ⵏⵙⴽⴰⵔ ⵜⴰⵙⵔⵜⵉⵜ ⴷ ⵏⵇⵇⵉⵎ ⵏⵎⵢⴰⵡⴰⴹ.*") : 
                   (cmsContent.newsletter_text_fr || "*Ici, on scrolle. On tag. On poste. On s’engage et on reste connecté.*")}
                </p>
                <div className="flex flex-col gap-3">
                  <Input 
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={lang === 'ar' ? 'البريد الإلكتروني...' : lang === 'zgh' ? 'ⵉⵎⴰⵢⵍ...' : 'Email...'} 
                    className="bg-background/10 border-background/20 text-background placeholder:text-background/40 focus-visible:ring-primary rounded-xl" 
                  />
                  <Button onClick={handleSubscribe} variant="default" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-xl gap-2 shadow-none">
                    {lang === 'ar' ? 'تسجيل' : lang === 'zgh' ? 'ⴰⵎⵓⵔ' : 'S\'inscrire'}
                    {isRtl ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Bottom: Copyright & Legals */}
          <div className="mt-16 pt-8 border-t border-background/10 flex flex-col lg:flex-row items-center justify-between gap-6 text-xs text-background/50 font-body">
            <p className="order-2 lg:order-1">{t.footer.copyright}</p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 order-1 lg:order-2">
              {displayMode === "unified" ? (
                <Link to="/mentions-legales" className="hover:text-primary transition-colors hover:underline underline-offset-4 font-bold">
                  {legalLinkTextUnified}
                </Link>
              ) : (
                <>
                  {legalContent["mentions_visible"] !== "false" && (
                    <Link to="/mentions-legales" className="hover:text-primary transition-colors hover:underline underline-offset-4">
                      {mentionsTitle}
                    </Link>
                  )}
                  {legalContent["conditions_visible"] !== "false" && (
                    <Link to="/conditions-utilisation" className="hover:text-primary transition-colors hover:underline underline-offset-4">
                      {conditionsTitle}
                    </Link>
                  )}
                  {legalContent["politique_visible"] !== "false" && (
                    <Link to="/politique-confidentialite" className="hover:text-primary transition-colors hover:underline underline-offset-4">
                      {politiqueTitle}
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
